import { StoreLink } from '@/components/store';
import type { CategoryPromoSlot } from '@/lib/category-promo-slots';

// A bare relative path from the backend ("montering-inskjutning") needs a
// leading slash before it's a valid StoreLink href; an already-absolute one
// is left alone.
export function normalizePromoLink(linkUrl: string): string {
  return linkUrl.startsWith('/') ? linkUrl : `/${linkUrl}`;
}

// Diagonal-stripe fill for a promo tile with no image yet (measured from the
// design canvas, which uses this exact pattern rather than a flat block for
// its own placeholder tiles). Inline rather than a Tailwind utility so it can
// reference the theme's raw surface tokens directly.
const PLACEHOLDER_PATTERN = {
  backgroundImage:
    'repeating-linear-gradient(-45deg, rgb(var(--surface-300)) 0, rgb(var(--surface-300)) 8px, rgb(var(--surface-400)) 8px, rgb(var(--surface-400)) 16px)',
};

// Shared by the desktop mega menu (navigation.tsx) and the mobile drawer
// (mobile-navigation.tsx), so both promotional treatments stay in sync.
export function PromoTile({ slot, onClick }: { slot: CategoryPromoSlot; onClick?: () => void }) {
  return (
    <StoreLink
      href={normalizePromoLink(slot.linkUrl)}
      onClick={onClick}
      className="group flex w-full flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary-500/50"
    >
      {slot.image ? (
        // Plain <img>, not next/image: this is a merchant-entered URL, not a
        // known Magento media host, so it must not be able to 500 the header.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={slot.image}
          alt={slot.imageAlt ?? slot.heading}
          loading="lazy"
          className="aspect-[5/3] w-full object-cover"
        />
      ) : (
        <div className="aspect-[5/3] w-full" style={PLACEHOLDER_PATTERN} aria-hidden="true" />
      )}
      <div className="flex flex-col gap-1 px-[18px] py-4">
        <p className="text-[14.5px] font-semibold leading-snug text-foreground">{slot.heading}</p>
        <span className="text-xs font-semibold uppercase tracking-[0.04em] text-accent-foreground transition-colors group-hover:text-primary-500">
          {slot.linkText} →
        </span>
      </div>
    </StoreLink>
  );
}
